import React, { useState, useEffect } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { Button, Spinner, Switch } from 'UI';
import { Form } from 'antd';
import { useDispatch } from 'hooks/store';
import {
  getVarietyProductsAction,
  updateVarietyAction,
} from 'state/slices/products';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { unwrapResult } from '@reduxjs/toolkit';
import { Variety } from 'constants/interface';
import { ProductTypeEnum } from 'constants/product';
import { UpdateVarietyData } from 'api/product';
import { useParams } from 'react-router';

// import AddCultureForm, { AddCultureFormData } from './components/AddCultureForm';
import AddForm, { AddVarietyFormData } from './components/AddForm';
import AddProductForm, { AddProductFormData } from './components/AddProductForm';
import {
  MainSectionContainer,
  ProductSectionContainer,
  ProductsTitle,
  ProductTypeContainer,
  SwitchContainer,
  SwitchLabelContainer,
} from './style';

interface ProductDetailPageRouteParams {
  varietyId: string
}

interface ProductDetailPageProps {
  onUpdateFinish?(): void
}

const ProductDetailPage = ({
  onUpdateFinish,
}: ProductDetailPageProps): JSX.Element => {
  const [addVarietyForm] = Form.useForm<AddVarietyFormData>();
  const [addStandardProductForm] = Form.useForm<AddProductFormData>();
  const [addScionProductForm] = Form.useForm<AddProductFormData>();
  const [addStockProductForm] = Form.useForm<AddProductFormData>();
  const [isLoading, setLoading] = useState(false);
  const [isFetchingVariety, setFetchingVariety] = useState(true);
  const navigator = useBreadcrumbNavigation();
  const dispatch = useDispatch();

  // const cultureEntities = useSelector(culturesEntitiesSelector);
  // const cultureIds = useSelector(culturesIdsSelector);
  // const cultureList = cultureIds.map((id) => cultureEntities[id]) as Culture[];
  // const isLoadingCultures = useSelector(isLoadingCulturesSelector);
  // const [selectedCulture, setSelectedCulture] = useState<Culture>();
  const [currentVariety, setCurrentVariety] = useState<Variety | undefined>();
  const [
    initialStandardProduct,
    setInitialStandardProduct,
  ] = useState<AddProductFormData | undefined>();
  const [
    initialScionProduct,
    setInitialScionProduct,
  ] = useState<AddProductFormData | undefined>();
  const [
    initialStockProduct,
    setInitialStockProduct,
  ] = useState<AddProductFormData | undefined>();

  const [isStandardActive, setStandardActive] = useState(false);
  const [isScionActive, setScionActive] = useState(false);
  const [isStockActive, setStockActive] = useState(false);

  const { varietyId } = useParams<ProductDetailPageRouteParams>();

  useEffect(() => {
    const fetchVariety = async () => {
      const result = await dispatch(getVarietyProductsAction({
        varietyId,
      }));
      const products = await unwrapResult(result);
      setCurrentVariety(products.scion.variety);

      setInitialStandardProduct(products.standard);
      setStandardActive(products.standard.isActive);

      setInitialScionProduct(products.scion);
      setScionActive(products.scion.isActive);

      setInitialStockProduct(products.stock);
      setStockActive(products.stock.isActive);

      setFetchingVariety(false);
    };
    fetchVariety();
  }, []);

  const onTypeToggle = (type: ProductTypeEnum, checked: boolean) => {
    if (type === ProductTypeEnum.STOCK) {
      if (checked) {
        setStandardActive(false);
        setScionActive(false);
        setStockActive(true);
      } else {
        setStockActive(false);
      }
    } else if (type === ProductTypeEnum.STANDARD) {
      if (checked) {
        setStandardActive(true);
        setStockActive(false);
      } else {
        setStandardActive(false);
      }
    } else if (type === ProductTypeEnum.SCION) {
      if (checked) {
        setScionActive(true);
        setStockActive(false);
      } else {
        setScionActive(false);
      }
    }
  };

  const onVarietySaveClick = async () => {
    try {
      await addVarietyForm.validateFields();
      if (isStandardActive) {
        await addStandardProductForm.validateFields();
      }
      if (isScionActive) {
        await addScionProductForm.validateFields();
      }
      if (isStockActive) {
        await addStockProductForm.validateFields();
      }
      // if (!currentVariety?.id) {
      //   throw new Error();
      // }
    } catch (err) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    setLoading(true);
    const basicData = addVarietyForm.getFieldsValue();
    const productStandardData = addStandardProductForm.getFieldsValue();
    const productScionData = addScionProductForm.getFieldsValue();
    const productStockData = addStockProductForm.getFieldsValue();
    const varietyFullData: UpdateVarietyData = {
      id: varietyId,
      data: {
        name: basicData.name,
        seedConsumption: basicData.seedConsumption,
        notes: basicData.notes,
        productStandard: {
          periodFormation: productStandardData.periodFormation,
          periodGermination: productStandardData.periodGermination,
          loss: (productStandardData.loss || 0) / 100.0,
          isActive: isStandardActive,
        },
        productScion: {
          periodFormation: productScionData.periodFormation,
          periodGermination: productScionData.periodGermination,
          periodGrafting: productScionData.periodGermination,
          periodHealing: productScionData.periodGermination,
          loss: (productScionData.loss || 0) / 100.0,
          isActive: isScionActive,
        },
        productStock: {
          periodFormation: productStockData.periodFormation,
          periodGermination: productStockData.periodGermination,
          periodGrafting: productStockData.periodGermination,
          periodHealing: productStockData.periodGermination,
          loss: (productStockData.loss || 0) / 100.0,
          isActive: isStockActive,
        },
        isActive: true, // TODO: get switch value
      },
    };

    try {
      const result = await dispatch(updateVarietyAction(varietyFullData));
      await unwrapResult(result);
      const successAlert = createAlertNotification(`
        Variedade atualizada com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      if (onUpdateFinish) onUpdateFinish();
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar atualizar variedade. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage
      title={`${currentVariety?.culture?.name} ${currentVariety?.name}`}
      extra={(
        <Button
          loading={isLoading}
          onClick={onVarietySaveClick}
        >
          Salvar produto
        </Button>
      )}
    >
      <>
        { isFetchingVariety ? (
          <Spinner />
        ) : (
          <>
            <MainSectionContainer>
              <AddForm
                formHandler={addVarietyForm}
                onSubmit={onVarietySaveClick}
                isLoading={isLoading}
                varietyBasicData={currentVariety}
              />
            </MainSectionContainer>
            <ProductSectionContainer>
              <ProductsTitle>Selecione os tipos de formação</ProductsTitle>
              <ProductTypeContainer>
                <SwitchContainer>
                  <Switch
                    checked={isStandardActive}
                    onChange={(checked) => onTypeToggle(ProductTypeEnum.STANDARD, checked)}
                  />
                  <SwitchLabelContainer>Pé franco</SwitchLabelContainer>
                </SwitchContainer>
                <AddProductForm
                  formHandler={addStandardProductForm}
                  onSubmit={onVarietySaveClick}
                  isLoading={isLoading}
                  isActive={isStandardActive}
                  productData={initialStandardProduct}
                />
              </ProductTypeContainer>
              <ProductTypeContainer>
                <SwitchContainer>
                  <Switch
                    checked={isScionActive}
                    onChange={(checked) => onTypeToggle(ProductTypeEnum.SCION, checked)}
                  />
                  <SwitchLabelContainer>Enxerto</SwitchLabelContainer>
                </SwitchContainer>
                <AddProductForm
                  formHandler={addScionProductForm}
                  onSubmit={onVarietySaveClick}
                  isLoading={isLoading}
                  isGrafting
                  isActive={isScionActive}
                  productData={initialScionProduct}
                />
              </ProductTypeContainer>
              <ProductTypeContainer>
                <SwitchContainer>
                  <Switch
                    checked={isStockActive}
                    onChange={(checked) => onTypeToggle(ProductTypeEnum.STOCK, checked)}
                  />
                  <SwitchLabelContainer>Porta-enxerto</SwitchLabelContainer>
                </SwitchContainer>
                <AddProductForm
                  formHandler={addStockProductForm}
                  onSubmit={onVarietySaveClick}
                  isLoading={isLoading}
                  isGrafting
                  isActive={isStockActive}
                  productData={initialStockProduct}
                />
              </ProductTypeContainer>
            </ProductSectionContainer>
          </>
        )}
      </>
    </SectionBasePage>
  );
};

ProductDetailPage.defaultProps = {
  onUpdateFinish: () => {},
};

export default ProductDetailPage;
